<template>
  <div class="asset-details-card" :class="{ 'asset-details-card--fill': fill }">
    <div>
      <div class="asset-details-card__year">{{ asset.year }}</div>
      <div class="asset-details-card__title">{{ asset.title }}</div>
    </div>

    <div class="asset-details-card__grid">
      <span class="asset-details-card__key">VIN</span>
      <span class="asset-details-card__value asset-details-card__value--code">{{ asset.vin }}</span>

      <span class="asset-details-card__key">Run · Lane</span>
      <span class="asset-details-card__value asset-details-card__value--strong">
        {{ asset.lotNumber }} · {{ asset.lane }}
        <span v-if="asset.timesRun > 0" class="asset-details-card__times-run">
          Tms Run {{ String(asset.timesRun).padStart(2, '0') }}
        </span>
      </span>

      <span class="asset-details-card__key">Stock #</span>
      <span class="asset-details-card__value">{{ asset.stockNumber }}</span>

      <span class="asset-details-card__key">Miles</span>
      <span class="asset-details-card__value">{{ (asset.miles || 0).toLocaleString() }}</span>

      <span class="asset-details-card__key">Color</span>
      <span class="asset-details-card__plain">{{ asset.color }}</span>

      <span class="asset-details-card__key">Drv · Cyl · Trn · A/C</span>
      <span class="asset-details-card__value asset-details-card__value--code">
        {{ asset.drivetrain }} · {{ asset.cylinders }}cyl · {{ asset.transmission }} ·
        {{ asset.airConditioning ? 'Y' : 'N' }}
      </span>

      <span class="asset-details-card__key">Seller</span>
      <span class="asset-details-card__seller">
        {{ asset.seller }}
        <span class="asset-details-card__seller-badge">{{ asset.sellerBadge }}</span>
      </span>
    </div>

    <ValuationStrip
      :black-book-value="valuation.blackBookValue"
      :market-report-value="valuation.marketReportValue"
      :reserve-value="valuation.reserveValue"
    />
  </div>
</template>

<script setup>
import ValuationStrip from '../ValuationStrip/ValuationStrip.vue';

// The lot's spec sheet — year and title over the field grid the legacy console
// laid out, closed by the valuation strip.
//
// Density is fixed compact. There is one console and one
// density on it; a density prop would be an option nobody would ever set.
defineOptions({ name: 'AssetDetailsCard' });

defineProps({
  // { year, title, vin, lotNumber, lane, timesRun, stockNumber, miles, color,
  //   drivetrain, cylinders, transmission, airConditioning, seller, sellerBadge }
  // `title` is the pre-joined make and model — the application owns how a
  // asset names itself.
  asset: { type: Object, required: true },
  // { blackBookValue, marketReportValue, reserveValue }, passed to the strip.
  valuation: { type: Object, default: () => ({}) },
  // Grow to fill the column it sits in, rather than sizing to its content.
  fill: { type: Boolean, default: false },
});
</script>

<style scoped>
.asset-details-card {
  display: flex;
  flex-direction: column;
  background: var(--p-asset-details-card-background);
  border: var(--p-asset-details-card-border-width) solid var(--p-asset-details-card-border-color);
  border-radius: var(--p-asset-details-card-border-radius);
  padding: var(--p-asset-details-card-padding);
  gap: var(--p-asset-details-card-gap);
}

.asset-details-card--fill {
  flex: 1;
  min-height: 0;
}

.asset-details-card__year {
  font-size: var(--p-asset-details-card-year-font-size);
  font-weight: var(--p-asset-details-card-year-font-weight);
  letter-spacing: var(--p-asset-details-card-year-letter-spacing);
  color: var(--p-asset-details-card-year-color);
  text-transform: uppercase;
}

.asset-details-card__title {
  margin-top: 2px;
  text-wrap: pretty;
  font-size: var(--p-asset-details-card-title-font-size);
  font-weight: var(--p-asset-details-card-title-font-weight);
  line-height: var(--p-asset-details-card-title-line-height);
  color: var(--p-asset-details-card-title-color);
}

.asset-details-card__grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--p-asset-details-card-grid-gap);
  font-size: var(--p-asset-details-card-grid-font-size);
  padding-top: var(--p-asset-details-card-grid-padding-top);
  border-top: var(--p-asset-details-card-grid-border-top-width) solid
    var(--p-asset-details-card-grid-border-top-color);
}

.asset-details-card__key {
  color: var(--p-asset-details-card-key-color);
  font-weight: var(--p-asset-details-card-key-font-weight);
}

.asset-details-card__value {
  font-family: var(--p-asset-details-card-value-font-family);
  font-weight: var(--p-asset-details-card-value-font-weight);
  color: var(--p-asset-details-card-value-color);
}

/* VIN and the drivetrain string are read character by character against a
   printed sheet, which is what the tabular figures and extra tracking are for. */
.asset-details-card__value--code {
  letter-spacing: var(--p-asset-details-card-value-code-letter-spacing);
  font-variant-numeric: tabular-nums;
}

.asset-details-card__value--strong {
  font-weight: var(--p-asset-details-card-value-strong-font-weight);
}

.asset-details-card__plain {
  color: var(--p-asset-details-card-plain-color);
}

.asset-details-card__times-run {
  margin-left: 8px;
  font-size: var(--p-asset-details-card-times-run-font-size);
  font-weight: var(--p-asset-details-card-times-run-font-weight);
  color: var(--p-asset-details-card-times-run-color);
}

.asset-details-card__seller {
  display: flex;
  align-items: center;
  gap: var(--p-asset-details-card-seller-gap);
  color: var(--p-asset-details-card-seller-color);
}

.asset-details-card__seller-badge {
  font-family: var(--p-asset-details-card-seller-badge-font-family);
  font-size: var(--p-asset-details-card-seller-badge-font-size);
  color: var(--p-asset-details-card-seller-badge-color);
  padding: var(--p-asset-details-card-seller-badge-padding);
  border-radius: var(--p-asset-details-card-seller-badge-border-radius);
  background: var(--p-asset-details-card-seller-badge-background);
}
</style>
